/**
 * Reporting-redesign data hooks (live-query v1).
 *
 * Thin wrappers over the new `reporting.*` tRPC procedures. Each hook is
 * independently `enabled`-gated so a tab fetches ONLY its own data, and only
 * when it is the active tab. Hooks fire in parallel within a tab (separate
 * `useQuery` calls, never chained), so a tab's round-trips overlap instead of
 * waterfalling. Outputs are already fully typed + numeric from the backend, so
 * there is no string coercion here (unlike the legacy consumption hooks).
 */
import { trpc } from '@/lib/trpc';
import { trpcErrorCode } from '@/lib/trpc-error-utils';

// S0-3: reporting procedures no longer accept a client-supplied organizationId
// (scope is derived server-side from the auth context). `organizationId` is kept
// on the param objects below only to GATE the query (don't fetch until an org is
// selected) — it is never sent in the query input.
const STALE = 5 * 60 * 1000; // 5 min — matches existing reporting hooks.

interface PeriodParams {
  organizationId: string | null;
  startDate: Date;
  endDate: Date;
  locationId: string | null;
  enabled?: boolean;
}

interface LocationParams {
  organizationId: string | null;
  locationId: string | null;
  enabled?: boolean;
}

interface AsOfParams {
  organizationId: string | null;
  asOfDate: Date | null;
  locationId: string | null;
  enabled?: boolean;
}

const gate = (organizationId: string | null, enabled?: boolean) =>
  enabled !== false && Boolean(organizationId);

const common = (organizationId: string | null, enabled?: boolean) => ({
  enabled: gate(organizationId, enabled),
  staleTime: STALE,
});

export function useOverviewSummary(p: PeriodParams) {
  return trpc.reporting.getOverviewSummary.useQuery(
    {
      startDate: p.startDate.toISOString(),
      endDate: p.endDate.toISOString(),
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useStockValueSummary(p: AsOfParams) {
  return trpc.reporting.getStockValueSummary.useQuery(
    {
      asOfDate: p.asOfDate ? p.asOfDate.toISOString() : null,
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useInventoryAging(p: LocationParams) {
  return trpc.reporting.getInventoryAging.useQuery(
    {
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useInventoryValueMovement(p: PeriodParams) {
  return trpc.reporting.getInventoryValueMovement.useQuery(
    {
      startDate: p.startDate.toISOString(),
      endDate: p.endDate.toISOString(),
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useBlankConsumption(p: PeriodParams) {
  return trpc.reporting.getBlankConsumption.useQuery(
    {
      startDate: p.startDate.toISOString(),
      endDate: p.endDate.toISOString(),
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function usePrintThroughput(p: PeriodParams) {
  return trpc.reporting.getPrintThroughput.useQuery(
    {
      startDate: p.startDate.toISOString(),
      endDate: p.endDate.toISOString(),
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useSellThroughByCategory(p: PeriodParams) {
  return trpc.reporting.getSellThroughByCategory.useQuery(
    {
      startDate: p.startDate.toISOString(),
      endDate: p.endDate.toISOString(),
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useTopMovers(p: PeriodParams & { limit?: number }) {
  return trpc.reporting.getTopMovers.useQuery(
    {
      startDate: p.startDate.toISOString(),
      endDate: p.endDate.toISOString(),
      locationId: p.locationId ?? null,
      limit: p.limit ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useSkuVelocity(p: PeriodParams) {
  return trpc.reporting.getSkuVelocity.useQuery(
    {
      startDate: p.startDate.toISOString(),
      endDate: p.endDate.toISOString(),
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useInventorySkuVelocity(p: PeriodParams) {
  return trpc.reporting.getInventorySkuVelocity.useQuery(
    {
      startDate: p.startDate.toISOString(),
      endDate: p.endDate.toISOString(),
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useAbcAnalysis(p: PeriodParams) {
  return trpc.reporting.getAbcAnalysis.useQuery(
    {
      startDate: p.startDate.toISOString(),
      endDate: p.endDate.toISOString(),
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useReorderSignals(p: LocationParams) {
  return trpc.reporting.getReorderSignals.useQuery(
    {
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

export function useOnOrderSummary(p: LocationParams) {
  return trpc.reporting.getOnOrderSummary.useQuery(
    {
      locationId: p.locationId ?? null,
    },
    common(p.organizationId, p.enabled),
  );
}

interface ThreePlUsageParams {
  organizationId: string | null;
  locationId: string | null;
  /** Inclusive local calendar days, `YYYY-MM-DD`. */
  from: string;
  to: string;
  enabled?: boolean;
}

/**
 * 3PL usage: ONE call per (location, period). Never retried and never
 * refetched on window focus: a timed-out read keeps running on the server, so
 * a retry or a focus refetch would run it twice. Everything else on the tab
 * (client, bill line, search, sort, chart grouping, CSV) is in memory.
 */
export function useThreePlUsage(p: ThreePlUsageParams) {
  return trpc.reporting.getThreePlUsage.useQuery(
    {
      locationId: p.locationId ?? '',
      from: p.from,
      to: p.to,
    },
    {
      ...common(p.organizationId, p.enabled !== false && p.locationId != null),
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * The 3PL's two bill-line writes (owner `skus.costs_manage` holders only,
 * D28): add a service to a bill, remove a hand-added line. Each is ONE call,
 * never retried (a timed-out add keeps running on the server, so a retry could
 * add the line twice).
 *
 * A success marks the usage report stale, which refetches the one mounted
 * report query once. `onSuccess` returns that promise, so a caller's own
 * success callback runs once the bill on screen already shows the change (the
 * dialog closes onto fresh data, DESIGN.md §12.2). A NOT_FOUND also refetches:
 * the line was already removed, or the service is gone, so the bill or its
 * service list on screen is out of date.
 */
export function useThreePlBillLines() {
  const utils = trpc.useUtils();
  const refresh = () => utils.reporting.getThreePlUsage.invalidate();
  const refreshIfGone = (error: unknown) =>
    trpcErrorCode(error) === 'NOT_FOUND' ? refresh() : undefined;

  const addLine = trpc.reporting.addThreePlBillLine.useMutation({
    retry: false,
    onSuccess: refresh,
    onError: refreshIfGone,
  });
  const removeLine = trpc.reporting.deleteThreePlBillLine.useMutation({
    retry: false,
    onSuccess: refresh,
    onError: refreshIfGone,
  });

  return { addLine, removeLine };
}
